/**
 * Permissions table component for access management.
 *
 * This is a convenience wrapper around DataTable that handles
 * permissions-specific rendering with category grouping.
 */
import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/data-table/data-table';

import { PermissionRow } from '../../types/permission';

/**
 * Props interface for the PermissionsTable component.
 */
interface PermissionsTableProps {
  /** Configured React Table instance with permission data and columns */
  table: Table<PermissionRow>;
  /** If true, table header becomes sticky and body scrolls independently */
  stickyHeader?: boolean;
  /** Grouped permissions by category for rendering headers */
  groupedPermissions: Array<{ category: string; items: PermissionRow[] }>;
}

/**
 * Permissions table component with category grouping.
 *
 * Wraps DataTable with permissions-specific configuration for
 * displaying permissions grouped by category with proper headers.
 */
export const PermissionsTable: React.FC<PermissionsTableProps> = ({
  table,
  stickyHeader = false,
  groupedPermissions,
}) => {
  const { t } = useTranslation('authenticated.settings');

  return (
    <DataTable
      table={table}
      stickyHeader={stickyHeader}
      groupedData={groupedPermissions}
      renderGroupHeader={category => (
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t(`pages.permissions.categories.${category}`)}
        </div>
      )}
    />
  );
};
